import React, {
  useState,
  useCallback,
  useMemo,
  createContext,
  useContext,
  ReactNode,
  ReactElement,
  ButtonHTMLAttributes
} from 'react';
import { Button, IconButton } from '@mui/material';
import clsx from 'clsx';
import './index.css';

interface ExpandableContextProps {
  isOpen: boolean;
  toggle: () => void;
}

const ExpandableContext = createContext<ExpandableContextProps>({
  isOpen: false,
  toggle: () => null
});

const useExpandableState = () => {
  const context = useContext(ExpandableContext);
  return context;
};

interface ExpandableProps {
  children: ReactNode;
}

const Expandable: React.FC<ExpandableProps> & {
  Closed: React.FC<{ children: ReactNode }>;
  Open: React.FC<{ children: ReactElement | null }>;
  OpenButton: React.FC<OpenButtonProps>;
  CloseButton: React.FC<{ children: ReactNode }>;
} = (props) => {
  const { children } = props;
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen((isOpen) => !isOpen), [setIsOpen]);

  const value = useMemo(() => ({ isOpen, toggle }), [isOpen, toggle]);

  return (
    <ExpandableContext.Provider value={value}>
      <div className={clsx({'closedContainer': !isOpen })}>{children}</div>
    </ExpandableContext.Provider>
  );
};

const Closed: React.FC<{ children: ReactNode }> = (props) => {
  const { children } = props;
  const { isOpen } = useExpandableState();
  return !isOpen ? <div className='flex-grow'>{children}</div> : null;
};

const Open: React.FC<{ children: ReactElement | null }> = (props) => {
  const { children } = props;
  const { isOpen } = useExpandableState();
  return isOpen ? children : null;
};

interface OpenButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  onClick?: () => void;
  shouldToggle?: boolean;
  disabled?: boolean;
  Wrapper?: React.ComponentType<any>;
}

const OpenButton: React.FC<OpenButtonProps> = (props) => {
  const {
    children,
    onClick,
    shouldToggle = true,
    disabled = false,
    Wrapper
  } = props;
  const { isOpen, toggle } = useExpandableState();

  const onClickButton = () => {
    if (onClick) onClick();
    if (shouldToggle) toggle();
  };

  const button = (
    <IconButton
      onClick={onClickButton}
      color='primary'
      disabled={disabled}
    >
      {children}
    </IconButton>
  );

  return !isOpen ? (
    Wrapper ? (
      <Wrapper>
        <span>{button}</span>
      </Wrapper>
    ) : (
      button
    )
  ) : null;
};

const CloseButton: React.FC<{ children: ReactNode }> = (props) => {
  const { children } = props;
  const { isOpen, toggle } = useExpandableState();

  return isOpen ? (
    <div className='closeButtonDiv'>
      <Button variant='outlined' onClick={toggle} type='button' sx={{marginRight: '10px'}}>
        Cancel
      </Button>
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, {
              ...(child && child.props),
              onClick: async () => {
                if ((child as React.ReactElement).props.onClick) {
                  await (child as React.ReactElement).props.onClick(toggle);
                }
              }
            })
          : null
      )}
    </div>
  ) : null;
};

Expandable.Closed = Closed;
Expandable.Open = Open;
Expandable.OpenButton = OpenButton;
Expandable.CloseButton = CloseButton;

export default Expandable;
